import { useContext } from "react"

import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import styled, { css } from "styled-components"

import InnerWrapper from "components/InnerWrapper"
import { ScreenContext } from "components/Providers"
import Callout from "components/TextElements/Callout"
import colors from "styles/colors"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"
import media from "styles/media"

export default function Platform() {
  const { tablet } = useContext(ScreenContext)
  const { t } = useTranslation()
  const { language } = useI18next()
  const mainCardAltText = t("Card with Galileo logo and graphics on it")

  const homePlatform: Queries.HomePlatformQuery = useStaticQuery(graphql`
    query HomePlatform {
      card: file(relativePath: { eq: "homepage/cardIllo.webp" }) {
        ...LocalImageQuery
      }
    }
  `)

  const imageSrc = useMedia(
    homePlatform.card?.childImageSharp?.gatsbyImageData,
    homePlatform.card?.childImageSharp?.gatsbyImageData,
    homePlatform.card?.childImageSharp?.gatsbyImageData,
    homePlatform.card?.childImageSharp?.gatsbyImageData
  )

  return (
    <>
      <Wrapper>
        <InnerWrapper left={tablet}>
          <Content>
            {imageSrc && (
              <PlatformImage
                image={imageSrc}
                alt="Card with Galileo logo and graphics on it"
              />
            )}
            <Callout
              variant="darkMode"
              title={{
                content: t("One platform, endless solutions."),
                width: useMedia("811px", "48.855vw", undefined, "89.333vw"),
                size: useMedia(text.h2, text.h2, text.h4, text.h1Mobile),
              }}
              text={{
                content: t(
                  "Invent, build, launch, and scale your own unique financial services with our proven platform that’s secure and compliant from the ground up. Deliver seamless, modern banking experiences above the glass—from intuitive apps to embedded finance solutions—while we enable everything below the glass with a fully integrated, multi-product infrastructure."
                ),
                width: useMedia("627px", "37.771vw", undefined, undefined),
              }}
              kicker={{
                text:
                  t("Financial technology platform") ??
                  "Financial technology platform",
                icon: "cards",
              }}
              left={tablet}
              buttons={[
                {
                  text: t("Explore Our Platform") ?? "Explore Our Platform",
                  type: "secondary",
                  arrow: true,
                  route: checkedLocaleLink(
                    "/platform/galileo-open-apis",
                    language
                  ),
                },
              ]}
            />
          </Content>
        </InnerWrapper>
      </Wrapper>
      <WrapperMobile>
        {imageSrc && (
          <MobileImage image={imageSrc} alt={mainCardAltText} loading="lazy" />
        )}
      </WrapperMobile>
    </>
  )
}

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  background: ${colors.darkMode};
  overflow: hidden;

  ${fresponsive(css`
    padding-bottom: 80px;
  `)}

  ${ftablet(css`
    padding-top: 188px;
    height: 650px;
  `)}

  ${fmobile(css`
    height: initial;
  `)}
`

const Content = styled.div`
  display: flex;
`

const PlatformImage = styled(GatsbyImage)`
  position: relative;

  ${media.fullWidth} {
    width: 603px;
    height: 580px;
    margin-right: 63px;
    left: -50px;
  }
  ${media.desktop} {
    width: 38vw;
    height: 38vw;
    margin-top: 12vw;
    left: -4vw;
    top: -5vw;
  }
  ${media.tablet} {
    width: 39vw;
    height: 40vw;
    margin-top: 6vw;
    margin-right: 6vw;
    left: 1vw;
    top: -5vw;
  }
    ${media.mobile}{
    display:none;
    }
`

const WrapperMobile = styled.div`
  width: 100%;
  display: none;
  justify-content: center;
  background: ${colors.darkMode};

  ${ftablet(css`
    margin-top: -2px;
  `)}

  ${fmobile(css`
    padding-top: 10px;
    display: flex;
  `)}
`

const MobileImage = styled(GatsbyImage)`
  height: auto;
  object-fit: contain;
  filter: drop-shadow(2.185vw 4.371vw 8.741vw rgba(1, 3, 13, 0.3));
  ${fmobile(css`
    width: 300px;
    height: 300px;
    border-radius: 10px;
  `)}
`
